/* assets/cart-drawer-custom.js
   Custom drawer controller (mobile-first)
   - Ajax add/change
   - Refresh section rendering (cart-drawer-custom)
   - Keep drawer open after refresh
   - Coupon apply/remove WITHOUT leaving drawer
   - Sync header cart count
   - Avoid double-add / bundle forms
*/

(() => {
  const drawerSel = '[data-cdrawer]';
  const getDrawer = () => document.querySelector(drawerSel);
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* =========================
     BODY LOCK
  ========================= */
  const lockBody = () => {
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollBarWidth > 0) document.body.style.paddingRight = `${scrollBarWidth}px`;
  };

  const unlockBody = () => {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  };

  const open = () => {
    const d = getDrawer();
    if (!d) return;
    d.hidden = false;
    d.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cdrawer-open');
    lockBody();
  };

  const close = () => {
    const d = getDrawer();
    if (!d) return;
    d.setAttribute('aria-hidden', 'true');
    d.hidden = true;
    document.documentElement.classList.remove('cdrawer-open');
    unlockBody();
  };

  /* =========================
     CART JSON + HEADER COUNT
  ========================= */
  const fetchCart = async () => {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return await res.json();
  };

  const syncHeaderCartCount = async (cartJson = null) => {
    const cart = cartJson || (await fetchCart());
    const count = Number(cart.item_count || 0);
    const txt = count >= 100 ? '99+' : String(count);

    const candidates = document.querySelectorAll(
      'cart-count, [data-cart-count], .cart-count, .header__cart-count, .header-cart-count, [aria-label*="cart count"]'
    );

    candidates.forEach((el) => {
      el.textContent = txt;
      if (count === 0) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    });

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  };

  /* =========================
     SECTION REFRESH (keep open)
  ========================= */
  const refreshDrawer = async () => {
    const current = getDrawer();
    const wasOpen = current ? !current.hidden : false;

    const url = `${window.location.pathname}?section_id=cart-drawer-custom&_=${Date.now()}`;
    const res = await fetch(url, { headers: { Accept: 'text/html' } });
    const html = await res.text();

    const doc = parse(html);
    const next = doc.querySelector(drawerSel);

    if (next && current) current.replaceWith(next);

    if (wasOpen) open();
  };

  /* =========================
     COUPONS (stay in drawer)
  ========================= */
  const cartUpdateUrl = window.FoxTheme?.routes?.cart_update_url || '/cart/update.js';

  const setCouponError = (msg) => {
    const d = getDrawer();
    const el = d?.querySelector('.ccoupon__error');
    if (!el) return;

    if (!msg) {
      el.hidden = true;
      el.textContent = '';
    } else {
      el.hidden = false;
      el.textContent = msg;
    }
  };

  // ✅ PATCH B: persist codes for Liquid/UI via cart attribute
  const applyDiscountCodes = async (codesCsv) => {
    const fd = new FormData();
    fd.append('discount', codesCsv);
    fd.append('sections_url', window.location.pathname);

    // ✅ persist for Liquid / UI
    fd.append('attributes[cd_discount_codes]', codesCsv || '');

    const res = await fetch(cartUpdateUrl, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: fd
    });

    const data = await res.json().catch(() => ({}));

    await refreshDrawer();
    open();

    const cart = await fetchCart();
    await syncHeaderCartCount(cart);

    return data;
  };

  /* =========================
     CART MUTATIONS (add/change)
  ========================= */
  const postJSON = async (url, payload) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });

    await res.json().catch(() => ({}));

    await refreshDrawer();
    open();

    const cart = await fetchCart();
    await syncHeaderCartCount(cart);
  };

  // ✅ NEW: post full form data to /cart/add.js (keeps properties/selling_plan/bundle metadata)
  const postForm = async (url, formData) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    });

    await res.json().catch(() => ({}));

    await refreshDrawer();
    open();

    const cart = await fetchCart();
    await syncHeaderCartCount(cart);
  };

  const addItems = (items) => postJSON('/cart/add.js', { items });
  const addFromForm = (form) => {
    const fd = new FormData(form);
    // Shopify accepte /cart/add.js avec FormData (id, quantity, properties[...], selling_plan, etc.)
    return postForm('/cart/add.js', fd);
  };

  const changeLine = (line, quantity) => postJSON('/cart/change.js', { line, quantity });

  /* =========================================================
     SAFE ADD-TO-CART INTERCEPT (avoid bundles + avoid double add)
  ========================================================= */
  let addInFlight = false;

  const isBundleLikeForm = (form) => {
    // bundles often submit items[0][id] ... items[n][id]
    return !!form.querySelector('[name^="items["], [name^="items[" i]');
  };

  const shouldInterceptAddToCartForm = (form) => {
    if (isBundleLikeForm(form)) return false;

    const hasVariantId = !!form.querySelector('input[name="id"]');
    if (!hasVariantId) return false;

    const action = (form.getAttribute('action') || '').toLowerCase();

    if (action.includes('/cart/add')) return true;

    const looksLikeProductForm =
      form.matches('form[action=""], form[action="/"], form.product-form') ||
      !!form.querySelector('[type="submit"][name="add"], [data-add-to-cart], [name="add"]');

    if (looksLikeProductForm) return true;

    if (form.hasAttribute('data-cdrawer-add-to-cart')) return true;

    return false;
  };

  // Capture phase to run BEFORE theme handlers (prevents double-add)
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      if (!shouldInterceptAddToCartForm(form)) return;

      e.preventDefault();
      e.stopPropagation();

      if (addInFlight) return;
      addInFlight = true;

      // ✅ IMPORTANT: preserve full bundle metadata (properties, selling_plan, hidden inputs, etc.)
      addFromForm(form)
        .then(() => open())
        .catch(console.error)
        .finally(() => {
          addInFlight = false;
        });
    },
    true
  );

  /* =========================
     CLICK HANDLERS (drawer UI)
  ========================= */
  document.addEventListener('click', async (e) => {
    const t = e.target;

    if (t.closest('[data-cdrawer-close]') || t.closest('[data-cdrawer-overlay]')) {
      e.preventDefault();
      close();
      return;
    }

    if (t.closest('[data-cdrawer-checkout]')) {
      e.preventDefault();
      window.location.href = '/checkout';
      return;
    }

    // apply coupon
    const couponBtn = t.closest('.ccoupon__go');
    if (couponBtn) {
      e.preventDefault();
      e.stopPropagation();

      const drawer = getDrawer();
      const input = drawer?.querySelector('.ccoupon__input');
      const codesWrap = drawer?.querySelector('.cdiscounts');

      const newCode = (input?.value || '').trim();
      if (!newCode) {
        input?.focus();
        return;
      }

      const existingCsv = (codesWrap?.getAttribute('data-applied-codes') || '').trim();
      const existing = existingCsv ? existingCsv.split(',').map((s) => s.trim()).filter(Boolean) : [];

      const merged = Array.from(new Set([...existing, newCode]));
      setCouponError('');

      const resp = await applyDiscountCodes(merged.join(','));
      open();

      const bad = resp?.discount_codes?.find(
        (dc) => (dc.code || '').toLowerCase() === newCode.toLowerCase() && dc.applicable === false
      );
      if (bad) setCouponError('Code invalide ou non applicable.');

      return;
    }

    // ✅ PATCH A: remove coupon chip with CLEAR mode
    const removeBtn = t.closest('[data-cdrawer-discount-remove]');
    if (removeBtn) {
      e.preventDefault();
      e.stopPropagation();

      const codeToRemove = (removeBtn.dataset.code || '').trim();

      // ✅ If we don't know the code (allocations only), clear discounts
      if (codeToRemove === '__clear__') {
        await applyDiscountCodes('');
        open();
        return;
      }

      const drawer = getDrawer();
      const codesWrap = drawer?.querySelector('.cdiscounts');

      const existingCsv = (codesWrap?.getAttribute('data-applied-codes') || '').trim();
      const existing = existingCsv ? existingCsv.split(',').map((s) => s.trim()).filter(Boolean) : [];

      const remaining = existing.filter((c) => c.toLowerCase() !== codeToRemove.toLowerCase());
      setCouponError('');

      await applyDiscountCodes(remaining.join(','));
      open();
      return;
    }

    // upsell add (still ok as pure id)
    const upsell = t.closest('[data-cdrawer-upsell-add]');
    if (upsell) {
      e.preventDefault();
      e.stopPropagation();

      if (addInFlight) return;
      addInFlight = true;

      const id = Number(upsell.dataset.variantId);
      addItems([{ id, quantity: 1 }])
        .then(() => open())
        .catch(console.error)
        .finally(() => {
          addInFlight = false;
        });
      return;
    }

    // Upsell carousel nav
    const prev = t.closest('[data-upsell-prev]');
    const next = t.closest('[data-upsell-next]');
    if (prev || next) {
      e.preventDefault();

      const drawer = getDrawer();
      const rail = drawer?.querySelector('[data-upsell-rail]');
      if (!rail) return;

      const amount = Math.round(rail.clientWidth * 0.85);
      rail.scrollBy({
        left: next ? amount : -amount,
        behavior: 'smooth'
      });

      return;
    }

    // qty/remove per line
    const item = t.closest('.citem[data-line]');
    if (!item) return;

    const line = Number(item.dataset.line);
    const qtyInput = item.querySelector('[data-cdrawer-qty]');
    const currentQty = Number(qtyInput?.value || 0);

    if (t.closest('[data-cdrawer-remove]')) {
      e.preventDefault();
      e.stopPropagation();
      changeLine(line, 0).then(() => open()).catch(console.error);
      return;
    }

    if (t.closest('[data-cdrawer-minus]')) {
      e.preventDefault();
      e.stopPropagation();
      changeLine(line, Math.max(0, currentQty - 1)).then(() => open()).catch(console.error);
      return;
    }

    if (t.closest('[data-cdrawer-plus]')) {
      e.preventDefault();
      e.stopPropagation();
      changeLine(line, currentQty + 1).then(() => open()).catch(console.error);
      return;
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });

  /* =========================
     HEADER CART ICON => open custom drawer (avoid /cart page)
  ========================= */
  document.addEventListener(
    'click',
    async (e) => {
      const el = e.target;

      const a = el.closest('a[href]');
      const href = (a?.getAttribute('href') || '').trim();

      const isCartLink =
        href === '/cart' ||
        href.startsWith('/cart?') ||
        href.endsWith('/cart') ||
        href.includes('/cart?');

      const toggle = el.closest('[aria-controls="CartDrawer"], .header__icon--cart, [data-cart-toggle]');

      if (!isCartLink && !toggle) return;
      if (href && href.includes('checkout')) return;

      e.preventDefault();
      e.stopPropagation();

      await refreshDrawer();
      open();
    },
    true
  );
})();
